// `/admin/transactions/$id` (`spec/17-frontend.md` §8.2). Foco: **valor e status**.
//
// Desde WEB-75 este componente é o CONTEÚDO do `RouteDrawer` da rota-pai: o cabeçalho
// (título, subtítulo e o botão de fechar) é do drawer, e a lista continua desenhada
// embaixo, inclusive em deep link.
//
// As duas escritas invalidam `['transactions']` inteiro e NÃO alimentam o cache com a
// resposta: `POST /release-reserve` devolve a visão SELLER
// (`admin-reserve.ts`: `responseSchema = transactionObjectSchema`), que não tem `seller` e
// tem `acquirer` anulável, um `setQueryData` com ela apagaria campos que esta tela usa.
// Nenhuma das duas rotas de escrita aceita `mode`: elas acham a cobrança pelo id.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { isApiError } from '../api/errors.ts';
import {
  adminTransactionOptions,
  adminTransactionsKeys,
  adminTransactionTimelineOptions,
  refundTransaction,
  releaseReserve,
} from '../api/queries/admin-transactions.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { AdminTransactionDetailView } from '../features/admin-transactions/transaction-detail.tsx';

function AdminTransactionPage() {
  const { id } = Route.useParams();
  // O modo é filtro da tela, não toggle global (`spec/17` §4.4): ele vem do `search` da
  // rota-pai, que é quem monta o `ModeSelect`.
  const { mode } = useSearch({ from: '/admin/transactions' });
  const readMode = mode ?? 'live';
  const queryClient = useQueryClient();

  const detail = useQuery(adminTransactionOptions(id, readMode));
  // A timeline só é buscada quando a cobrança existe: sem isso um 404 renderizaria dois
  // blocos de erro para a mesma causa.
  const timeline = useQuery(adminTransactionTimelineOptions(id, readMode, detail.isSuccess));

  // `all` (`['transactions','admin']`) cobre detalhe, timeline e todas as listas abertas.
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: adminTransactionsKeys.all });
  };

  const refund = useMutation({
    mutationFn: (reason: string) => refundTransaction(id, reason),
    onSuccess: invalidate,
  });
  const release = useMutation({
    mutationFn: (reason: string) => releaseReserve(id, reason),
    onSuccess: invalidate,
  });

  if (detail.isPending) {
    return <CardSkeleton lines={5} />;
  }

  // 404 pelo `code`, nunca pelo texto do status (`spec/17` §4.3 item 1).
  if (isApiError(detail.error) && detail.error.code === 'resource_not_found') {
    return (
      <Empty
        icon="swap"
        title="Cobrança não encontrada"
        hint={`Este id não existe no modo ${readMode}. Troque o modo no cabeçalho da lista se a cobrança for de teste.`}
      />
    );
  }

  if (detail.error !== null || detail.data === undefined) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  return (
    <AdminTransactionDetailView
      transaction={detail.data}
      timeline={{
        items: timeline.data?.data,
        isPending: timeline.isPending,
        error: timeline.error,
        onRetry: () => {
          void timeline.refetch();
        },
      }}
      refund={{
        mutateAsync: refund.mutateAsync,
        isPending: refund.isPending,
        error: refund.error,
        reset: refund.reset,
      }}
      release={{
        mutateAsync: release.mutateAsync,
        isPending: release.isPending,
        error: release.error,
        reset: release.reset,
      }}
    />
  );
}

export const Route = createFileRoute('/admin/transactions/$id')({
  component: AdminTransactionPage,
});
